import { createContext, useEffect, useState } from 'react';
import { ChatbotWidgetConfigDto, ReceiveChatConversationMessagesDto } from '@endion/types';
import { getSanitizedCustomerChatbot } from '../queries/chatbot-client-config.query';
import { ENDION_CHATBOT_UPDATE_CONTEXT_EVENT_NAME } from '../utils/events';

export const CustomerChatbotContext = createContext<{
  isLoading: boolean;
  customerChatbot?: ChatbotWidgetConfigDto;
  chatbotApiKey: string;
  websiteProvidedContext?: ReceiveChatConversationMessagesDto['context'];
  showTrigger?: boolean;
}>({
  isLoading: true,
  chatbotApiKey: '',
  websiteProvidedContext: {},
  showTrigger: true,
});

export function CustomerChatbotContextProvider({
  children,
  chatbotApiKey,
  websiteProvidedContext: initialwebsiteProvidedContext,
  showTrigger = true,
}: {
  children: React.ReactNode;
  chatbotApiKey: string;
  websiteProvidedContext?: ReceiveChatConversationMessagesDto['context'];
  showTrigger?: boolean;
}) {
  const [customerChatbot, setChatCustomerChatbot] = useState<ChatbotWidgetConfigDto>();
  const [websiteProvidedContext, setwebsiteProvidedContext] =
    useState<ReceiveChatConversationMessagesDto['context']>(initialwebsiteProvidedContext);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async function () {
      const chatCurrentContext = await getSanitizedCustomerChatbot({ chatbotApiKey });
      setChatCustomerChatbot(chatCurrentContext);
      setIsLoading(false);
    })();
  }, [setChatCustomerChatbot, chatbotApiKey]);

  useEffect(() => {
    const handleEvent = (event: Event) => {
      const customEvent = event as CustomEvent<ReceiveChatConversationMessagesDto['context']>;
      const newContext = customEvent.detail;

      //! TODO: Add real validation based on zod
      if (typeof newContext !== 'object' || newContext === null) {
        console.warn('Invalid context received, expected an object.');
        return;
      }

      setwebsiteProvidedContext((prevContext = {}) => ({
        user: {
          ...prevContext.user,
          ...(newContext.user || {}),
        },
        realEstate: {
          ...prevContext.realEstate,
          ...(newContext.realEstate || {}),
        },
      }));
    };

    document.addEventListener(ENDION_CHATBOT_UPDATE_CONTEXT_EVENT_NAME, handleEvent);

    return () => {
      document.removeEventListener(ENDION_CHATBOT_UPDATE_CONTEXT_EVENT_NAME, handleEvent);
    };
  }, []);

  return (
    <CustomerChatbotContext.Provider
      value={{ customerChatbot, isLoading, chatbotApiKey, websiteProvidedContext, showTrigger }}
    >
      {children}
    </CustomerChatbotContext.Provider>
  );
}
